import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Users, MapPin, Sword, Shield, MessageCircle, AlertTriangle, DollarSign, Crown, Flag, UserX, UserCheck, ChevronLeft } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const AdminDashboard = ({ onBack }) => {
  const [adminKey, setAdminKey] = useState('');
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [players, setPlayers] = useState([]);
  const [flags, setFlags] = useState([]);
  const [territoryData, setTerritoryData] = useState(null);
  const [tab, setTab] = useState('overview');
  const [error, setError] = useState(null);

  const headers = { 'X-Admin-Key': adminKey };

  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/stats`, { headers });
      if (res.ok) { setStats(await res.json()); setAuthed(true); setError(null); }
      else { setError('Invalid admin key'); setAuthed(false); }
    } catch (e) { setError('Network error'); }
  }, [adminKey]);

  const loadPlayers = useCallback(async () => {
    const res = await fetch(`${API}/api/admin/players?limit=50`, { headers });
    if (res.ok) setPlayers((await res.json()).players || []);
  }, [adminKey]);

  const loadFlags = useCallback(async () => {
    const res = await fetch(`${API}/api/admin/flags`, { headers });
    if (res.ok) setFlags((await res.json()).flags || []);
  }, [adminKey]);

  const loadTerritoryMap = useCallback(async () => {
    const res = await fetch(`${API}/api/admin/territory-map`, { headers });
    if (res.ok) setTerritoryData(await res.json());
  }, [adminKey]);

  const banPlayer = async (gameId) => {
    await fetch(`${API}/api/admin/ban/${gameId}`, { method: 'POST', headers });
    loadPlayers();
  };

  const unbanPlayer = async (gameId) => {
    await fetch(`${API}/api/admin/unban/${gameId}`, { method: 'POST', headers });
    loadPlayers();
  };

  useEffect(() => {
    if (authed) { loadPlayers(); loadFlags(); loadTerritoryMap(); }
  }, [authed, loadPlayers, loadFlags, loadTerritoryMap]);

  // Auth screen
  if (!authed) {
    return (
      <div className="space-y-4" data-testid="admin-dashboard">
        <button onClick={onBack} className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest hover:text-[#C9A84C]">BACK</button>
        <div className="text-center">
          <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#E8E0D0]">ADMIN ACCESS</h3>
          <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest">AUTHORIZED PERSONNEL ONLY</p>
        </div>
        {error && <p className="font-['Outfit'] text-[#8B1A1A] text-xs text-center">{error}</p>}
        <div className="flex gap-2 max-w-xs mx-auto">
          <input type="password" value={adminKey} onChange={e => setAdminKey(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && loadStats()}
            placeholder="Admin Key"
            className="flex-1 px-3 py-2 bg-[#0a0c10] border border-[#1A2535] rounded-lg text-[#E8E0D0] font-['Outfit'] text-xs outline-none"
            data-testid="admin-key-input" />
          <button onClick={loadStats}
            className="px-4 py-2 bg-[#C9A84C]/10 border border-[#C9A84C]/30 rounded-lg text-[#C9A84C] font-['Outfit'] text-[9px] tracking-widest"
            data-testid="admin-login-btn">
            ENTER
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'OVERVIEW', icon: <Crown size={12} /> },
    { id: 'players', label: 'PLAYERS', icon: <Users size={12} /> },
    { id: 'territory', label: 'TERRITORY', icon: <MapPin size={12} /> },
    { id: 'flags', label: 'FLAGS', icon: <AlertTriangle size={12} /> },
  ];

  return (
    <div className="space-y-3" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest hover:text-[#C9A84C]">BACK</button>
        <span className="font-['Outfit'] text-[#8B1A1A] text-[8px] tracking-widest">ADMIN MODE</span>
      </div>

      {/* Tabs */}
      <div className="flex gap-1">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg font-['Outfit'] text-[8px] tracking-wider border ${
              tab === t.id ? 'bg-[#C9A84C]/10 text-[#C9A84C] border-[#C9A84C]/30' : 'text-[#E8E0D0]/30 border-[#1A2535]'
            }`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === 'overview' && stats && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {[
              { label: 'PLAYERS', value: stats.total_players, color: '#C9A84C' },
              { label: 'NEW 24H', value: stats.recent_players_24h, color: '#4ADE80' },
              { label: 'CREWS', value: stats.total_crews, color: '#4A7AB5' },
              { label: 'TERRITORIES', value: `${stats.territories_claimed}/${stats.territories_total}`, color: '#FFD700' },
              { label: 'WARS', value: `${stats.active_wars} active`, color: '#8B1A1A' },
              { label: 'CHALLENGES', value: stats.total_challenges, color: '#9B59B6' },
              { label: 'TABLE GAMES', value: stats.total_table_games, color: '#27AE60' },
              { label: 'CHAT MSGS', value: stats.chat_messages, color: '#E8E0D0' },
              { label: 'BOUNTIES', value: stats.active_bounties, color: '#E74C3C' },
              { label: 'AC FLAGS', value: stats.anti_cheat_flags, color: stats.anti_cheat_flags > 0 ? '#E74C3C' : '#4ADE80' },
            ].map((s, i) => (
              <div key={i} className="bg-[#111820] rounded-lg p-2.5 text-center">
                <p className="font-['Outfit'] text-[6px] text-[#E8E0D0]/20 tracking-widest">{s.label}</p>
                <p className="font-['Outfit'] text-sm font-bold" style={{ color: s.color }}>{s.value}</p>
              </div>
            ))}
          </div>
          <button onClick={loadStats}
            className="w-full py-1.5 bg-[#1A2535] rounded text-[#E8E0D0]/30 font-['Outfit'] text-[8px] tracking-widest hover:text-[#C9A84C]">
            REFRESH
          </button>
        </div>
      )}

      {/* Players */}
      {tab === 'players' && (
        <div className="space-y-1 max-h-[400px] overflow-y-auto">
          {players.map((p, i) => (
            <div key={i} className="flex items-center justify-between px-3 py-2 bg-[#111820] rounded-lg border border-[#1A2535]">
              <div>
                <span className="font-['Outfit'] text-xs text-[#E8E0D0]">{p.username || 'Anonymous'}</span>
                <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/20">{p.email} | Era {p.current_era} | ${p.cash?.toLocaleString()}</p>
                <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/10">{p.game_id}</p>
              </div>
              <div className="flex gap-1">
                {p.banned ? (
                  <button onClick={() => unbanPlayer(p.game_id)}
                    className="p-1.5 bg-[#4ADE80]/10 border border-[#4ADE80]/30 rounded text-[#4ADE80]">
                    <UserCheck size={12} />
                  </button>
                ) : (
                  <button onClick={() => banPlayer(p.game_id)}
                    className="p-1.5 bg-[#8B1A1A]/10 border border-[#8B1A1A]/30 rounded text-[#8B1A1A]">
                    <UserX size={12} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Territory */}
      {tab === 'territory' && territoryData && (
        <div className="space-y-3">
          <p className="font-['Outfit'] text-[#C9A84C] text-[9px] tracking-widest">TOP TERRITORY HOLDERS</p>
          <div className="space-y-1">
            {(territoryData.top_owners || []).map((o, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-1.5 bg-[#111820] rounded-lg">
                <span className="font-['Outfit'] text-xs text-[#E8E0D0]">#{i + 1} {o.name}</span>
                <div className="text-right">
                  <span className="font-['Outfit'] text-xs text-[#C9A84C]">{o.count} blocks</span>
                  <p className="font-['Outfit'] text-[8px] text-[#4ADE80]">${o.income?.toLocaleString()}/cycle</p>
                </div>
              </div>
            ))}
          </div>

          <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest mt-3">BOROUGH STATUS</p>
          <div className="grid grid-cols-2 gap-1.5">
            {Object.entries(territoryData.borough_stats || {}).map(([borough, data]) => (
              <div key={borough} className="bg-[#111820] rounded-lg p-2">
                <p className="font-['Outfit'] text-[8px] text-[#C9A84C] font-bold">{borough.replace(/_/g, ' ').toUpperCase()}</p>
                <div className="flex items-center gap-1 mt-1">
                  <div className="flex-1 h-1.5 bg-[#1A2535] rounded-full overflow-hidden">
                    <div className="h-full bg-[#C9A84C] rounded-full" style={{ width: `${(data.claimed / data.total) * 100}%` }} />
                  </div>
                  <span className="font-['Outfit'] text-[7px] text-[#E8E0D0]/30">{data.claimed}/{data.total}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Flags */}
      {tab === 'flags' && (
        <div className="space-y-1 max-h-[400px] overflow-y-auto">
          {flags.length === 0 ? (
            <p className="font-['Outfit'] text-[#4ADE80] text-xs text-center py-4">No anti-cheat flags. Clean streets.</p>
          ) : flags.map((f, i) => (
            <div key={i} className="px-3 py-2 bg-[#111820] border border-[#8B1A1A]/15 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-['Outfit'] text-xs text-[#8B1A1A]">{f.type}</span>
                <span className="font-['Outfit'] text-[8px] text-[#E8E0D0]/20">{f.flagged_at}</span>
              </div>
              <p className="font-['Outfit'] text-[9px] text-[#E8E0D0]/40">Player: {f.game_id} | Action: {f.action} | Count: {f.count}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
